import { createEffect, createSignal } from "solid-js";
import { fromActorRef } from "@xstate/solid";
import { useQuery, type UseQueryResult } from "@tanstack/solid-query";
import { useNavigate } from "@tanstack/solid-router";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { queryClient } from "./query-client";
import { api, writeSessionToken } from "./api";
import {
  cloneSession,
  type components,
  getActiveAccount,
  handleApiResponse,
  queryKeys,
  removeAccount as removeAccountReducer,
  safeRedirectTarget,
  type Session,
  sharedApi,
  type StoredAccount,
  VISITOR_ID,
} from "@famidex/shared";
import { clearSessionStorage, primeSessionForAccount, sessionActor } from "./session-machine";
import {
  accountsStore,
  buildAccountFromAuthResponse,
  pickDisplayName,
  readAccountsStateSync,
  readActiveAccountSync,
} from "./accounts";
import {
  indexOfUserId,
  parseActiveIndexFromPath,
  rememberLastActiveIndex,
  resolveActiveAccountView,
  resolveTabAccount,
} from "./active-account";
import { activePathname } from "./reactive-location";
import { SESSION_STALE_TIME_MS } from "./constants";

// Flag in-memory para prevenir condiciones de carrera (race conditions) donde
// limpiar la caché de React/Solid Query provoca un refetch inmediato
// antes de que el servidor haya invalidado la cookie.
const [isSigningOutIntent, setIsSigningOutIntent] = createSignal(false);

// Re-exporting from shared for convenience if needed, but using direct imports is preferred
export { cloneSession, safeRedirectTarget };

/**
 * Standalone REACTIVE accessor of the ACTIVE per-tab account id (URL `/u/<n>` →
 * registry), WITHOUT the `useSession()` hook ceremony. Mirrors the primary
 * (per-tab) branch of `useSession().userId` and matches the PowerSync provider's
 * DB binding. A guest tab (or no resolvable account) resolves to `VISITOR_ID`.
 *
 * Used by `createAccountPowerSyncQuery` to partition the SHARED TanStack cache
 * by account in ONE chokepoint — so a row cached under account A is never served
 * to account B. Being a plain function (not a `use*` hook) keeps the wrapper
 * free of rules-of-hooks constraints while staying reactive via `activePathname`.
 */
export function activeAccountUserId(): string {
  const index = parseActiveIndexFromPath(activePathname());
  if (index !== null) {
    const account = resolveTabAccount(readAccountsStateSync(), index);
    if (account) {
      return account.kind === "guest" ? VISITOR_ID : account.userId;
    }
  }
  return VISITOR_ID;
}

/**
 * Modern Session Hook using custom Go backend client.
 * Integrated with sessionActor for offline priming.
 */
export function useSession(): Omit<
  UseQueryResult<Session | null, components["schemas"]["ErrorModel"]>,
  "data" | "isPending" | "error"
> & {
  data: Session | undefined;
  userId: () => string;
  isAuthenticated: () => boolean;
  isVisitor: () => boolean;
  isPending: boolean;
  error: components["schemas"]["ErrorModel"] | null;
} {
  // Read reactive state from the machine using fromActorRef for Solid compatibility
  // Declared early so we can use it in the `enabled` guard below.
  const snapshot = fromActorRef(sessionActor);

  // Per-tab active account is encoded in the URL `/u/<n>` (see
  // `lib/active-account.ts`). Reading `location()` makes the derived `userId`
  // reactive to an IN-APP account switch (the switcher navigates to a new
  // `/u/<n>` WITHOUT a reload). This MUST agree with the PowerSync provider's
  // per-tab DB binding (also URL-driven): otherwise data queries would filter
  // by the global session machine's user (which holds only ONE identity and can
  // lag a switch) against a DB bound to a DIFFERENT per-tab user, returning
  // zero rows until a hard reload — the multi-account switch bug.
  //
  // Uses the router-agnostic `activePathname` singleton (NOT the router's
  // `useLocation()` hook): `useSession()`/`userId` is consumed by providers that
  // mount ABOVE `<RouterProvider>` (e.g. theme-provider), where `useLocation()`
  // throws "Cannot read properties of null". See `lib/reactive-location.ts`.

  const currentSession = () => snapshot().context.session;
  const isPriming = () => snapshot().value === "priming";
  const isVisitor = () => snapshot().context.isVisitor;
  const machineError = () => snapshot().context.error;

  // Fetch as soon as priming finishes — `isVisitor` starts as `true` after
  // priming whenever localStorage has no payload, so gating on `!isVisitor`
  // Creates a chicken-and-egg deadlock: the network call is exactly what
  // would prove we're NOT a visitor (via the backend's HttpOnly cookie),
  // but the gate prevents that call from ever happening.
  // Once the query settles, the machine receives SERVER_REPLY and either
  // hydrates the session (200) or confirms visitor mode (401/403).
  // We ALSO prevent fetching if we are intentionally signing out.
  const shouldFetch = () => !isPriming() && !isSigningOutIntent();

  const query = useQuery<Session | null, components["schemas"]["ErrorModel"]>(() => ({
    queryKey: queryKeys.auth.session(),
    queryFn: async () => {
      const result = await api.GET("/api/auth/get-session", {
        params: { query: { jwt: true } },
      });
      return result.data ?? null;
    },
    // Only fetch when we have a confirmed session
    enabled: shouldFetch(),
    // Preserve session in cache to avoid refetches on focus/tab-switch
    staleTime: SESSION_STALE_TIME_MS,
    // ABSOLUTELY NO RETRIES for auth failures — we handle them via machine
    retry: false,
    // Don't refetch on window focus — session changes are handled by the
    // machine and explicit sign-in/sign-out flows.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  }));

  // Sync server changes into the machine — matching apps/native parity.
  // This ensures that when refetchQueries is called after login, the
  // sessionActor is updated automatically.
  createEffect(() => {
    // BUGFIX: durante el sign-out, una respuesta de get-session aún en vuelo
    // (p. ej. el prefetch de _app/route) puede resolverse DESPUÉS del LOGOUT
    // y repoblar la sesión en la máquina; el BootManager entonces re-redirige
    // de /login a "/" y el usuario queda "deslogueado" dentro de la app.
    // Mientras exista intención de sign-out no sincronizamos réplicas stale.
    if (isSigningOutIntent()) {
      return;
    }
    if (!query.isPending) {
      // Multi-account: if the user explicitly chose the GUEST account, the
      // backend cookie may still authenticate a real account — but we must NOT
      // let that server reply revive a real session in the machine, or it would
      // flip `activeUserId` back to the real user and swap the active DB away
      // from the guest. Honor the explicit guest choice; the real session is
      // restored when the user switches back to that account (re-prime).
      const activeIsGuest = readActiveAccountSync()?.kind === "guest";
      if (activeIsGuest) {
        return;
      }
      sessionActor.send({
        type: "SERVER_REPLY",
        session: query.data ?? null,
        error: query.error,
      });
      // Multi-account: ensure the authenticated user is in the registry so it
      // appears in the switcher even when sign-in went through the auth-flow
      // merge dialog (which doesn't call `auth.signIn`). The per-account token is
      // filled in by the explicit `auth.signIn`/`signUp` paths when available.
      //
      // CRITICAL: use `syncProfile`, NOT `upsert`. The backend cookie can keep
      // authenticating a real account even AFTER the user switched to the guest
      // (or to another account), so this passive `get-session` reply must NOT
      // re-activate that account — `upsert` sets it active and would instantly
      // undo "Continue as guest" / an account switch. `syncProfile` registers /
      // refreshes the account but preserves the user's chosen active pointer.
      const { data } = query;
      if (data?.user.id) {
        void accountsStore.syncProfile({
          userId: data.user.id,
          email: data.user.email,
          displayName: pickDisplayName(data.user.name, data.user.email, data.user.id),
          avatar: data.user.image ?? undefined,
          kind: "real",
          // Persist the role so the Profile's Role badge survives a later
          // session clear (401) / an in-app switch's minimal session window.
          role: data.user.role ?? undefined,
        });
      }
    }
  });

  // Reset the sign-out intent flag once the user successfully authenticates again
  createEffect(() => {
    if (currentSession()) {
      setIsSigningOutIntent(false);
    }
  });

  /**
   * Helper to get the user ID safely from the session.
   */
  const userId = (): string => {
    // PRIMARY (reactive, per-tab): resolve the active account from the tab's URL
    // `/u/<n>` index against the shared registry. This is the per-tab source of
    // truth and matches the PowerSync provider's DB binding, so data queries
    // always filter by the SAME user the bound DB belongs to — making an in-app
    // account switch swap the displayed data without a reload.
    const index = parseActiveIndexFromPath(activePathname());
    if (index !== null) {
      const account = resolveTabAccount(readAccountsStateSync(), index);
      if (account) {
        return account.kind === "guest" ? VISITOR_ID : account.userId;
      }
    }

    // FALLBACK (boot / auth pages with no `/u/<n>` yet): the global session.
    const { data } = query;
    const current = currentSession();
    const effectiveSession = current ?? data;
    return effectiveSession?.user.id ?? VISITOR_ID;
  };

  // PER-TAB session VIEW (single source of truth for auth/role/identity gating).
  // Driven reactively by `activePathname()` → the active account (URL `/u/<n>` →
  // registry), mirroring `userId`.
  //
  // DESIGN: this LAYERS the per-tab view ON TOP of the baseline global-session
  // behavior, changing it in exactly two cases and leaving the boot/sign-out
  // machinery (BootManager, sync overlay — all of which read `isAuthenticated()`
  // / `data` / `isVisitor()`) byte-identical to before otherwise:
  //   • GUEST tab → unauthenticated, no user/role (the bug: a guest tab must NOT
  //     show the logged-in real user's surfaces).
  //   • a REAL account that is NOT the globally-primed one (a different account
  //     open in another tab, or the brief in-app-switch window) → its own
  //     identity + JWT-decoded role, instead of the wrong global identity.
  // A real account that MATCHES the global session, OR a `/u/<n>`-less boot/auth
  // route, falls through to the BASELINE global session — preserving every
  // existing 401 / sign-out / reload boot transition.
  const activeView = () => {
    const index = parseActiveIndexFromPath(activePathname());
    const globalSession = currentSession() ?? query.data ?? null;
    return resolveActiveAccountView(readAccountsStateSync(), index, globalSession);
  };

  // SINGLE source of truth for the per-tab identity actually displayed. `data`,
  // `isAuthenticated` and `isVisitor` ALL derive from this so they can never
  // disagree about "who is this tab" — the bug being fixed was `isAuthenticated`
  // gating ONLY on the GLOBAL session machine (`currentSession()`) while `data`
  // resolved the per-tab view: a tab whose active account is a REAL account that
  // is NOT the globally-primed one (another tab's account, or the brief window
  // after an account switch / before global priming, e.g. a guest-primed machine
  // deep-linked to a real account's `/u/<n>`) had `data` populated yet
  // `isAuthenticated()` false → Profile rendered "Guest Mode" while the switcher
  // correctly showed the account active.
  //   • GUEST tab → undefined (a guest is never authenticated).
  //   • REAL per-tab account → its identity view (the global session when it
  //     matches, else the JWT-built minimal view).
  //   • `/u/<n>`-less boot/auth route → the global session, falling back to the
  //     cached query data EXCEPT while signing out (so a stale/in-flight
  //     `get-session` reply can't revive an authenticated view post-logout).
  const resolvedData = (): Session | undefined => {
    const view = activeView();
    if (view.isGuest) {
      return undefined;
    }
    if (view.account) {
      return view.user;
    }
    const cachedFallback = isSigningOutIntent() ? undefined : query.data;
    return currentSession() ?? cachedFallback ?? undefined;
  };

  return {
    ...query,
    // Use getters to maintain reactivity for properties that depend on machine state
    get data() {
      return resolvedData();
    },
    userId,
    // Coherent with `data`: authenticated iff a real per-tab identity resolves.
    // A GUEST tab (or no resolved identity) is never authenticated, even while
    // the global machine still holds a real session (cookie / another tab).
    isAuthenticated: () => Boolean(resolvedData()),
    // Coherent with `data`: a visitor is exactly a tab with no resolved real
    // identity (an explicit guest, or boot/login before any session resolves).
    isVisitor: () => !resolvedData(),
    get isPending() {
      return isPriming() || (query.isPending && !currentSession() && !isVisitor());
    },
    get error() {
      return query.error ?? machineError();
    },
  };
}

/**
 * Persists the session bearer token so subsequent API calls can attach it as
 * `Authorization: Bearer …`. Required in cross-site WebView contexts (Tauri)
 * where the `SameSite=Lax` cookie is dropped.
 *
 * Accepts `unknown` because the auth endpoints return slightly different
 * shapes (sign-in → `AuthResponse`, sign-up → `UserDTO`, etc.) and we want
 * to extract the token defensively in all cases.
 */
function persistSessionFromResponse(res: unknown): void {
  if (typeof res !== "object" || res === null) {
    return;
  }
  if (!("session" in res)) {
    return;
  }
  const { session } = res;
  if (typeof session !== "object" || session === null) {
    return;
  }
  if (!("token" in session)) {
    return;
  }
  if (typeof session.token !== "string") {
    return;
  }
  writeSessionToken(session.token);
}

/**
 * Registers (or refreshes) the account from a sign-in/sign-up response in the
 * multi-account registry and makes it the active account. The per-account
 * Bearer token (the session token, which `auth_middleware` accepts) drives the
 * API client's `Authorization` header on subsequent requests.
 *
 * `fallbackToken` supplies the Bearer when the response body itself carries no
 * `session.token`. This is the OAuth case: the social callback receives the
 * opaque session token in the redirect fragment (`#token=…`), but the
 * `get-session` payload used to build the profile NEVER includes it (the public
 * session view omits the token by design). Without this, the OAuth account
 * landed token-LESS → under Bearer-only every authenticated request 401s and
 * PowerSync never connects. The email/password path already gets its token from
 * the sign-in response, so it passes no fallback.
 *
 * Returns the stored account (or `null` if the response lacked a usable id).
 */
export async function registerAccountFromResponse(
  res: unknown,
  fallbackToken?: string,
): Promise<StoredAccount | null> {
  const account = buildAccountFromAuthResponse(res, fallbackToken);
  if (account === null) {
    return null;
  }
  await accountsStore.upsert(account);
  return account;
}

/**
 * Bridge for common auth operations using the custom client and shared API logic.
 */
export const auth = {
  signIn: {
    email: async (body: components["schemas"]["AuthLoginBody"]) => {
      const res = await handleApiResponse(sharedApi.signIn(api, body));
      persistSessionFromResponse(res);
      await registerAccountFromResponse(res);
      return res;
    },
  },
  signUp: {
    email: async (body: components["schemas"]["AuthRegisterBody"]) => {
      const res = await handleApiResponse(sharedApi.signUp(api, body));
      persistSessionFromResponse(res);
      await registerAccountFromResponse(res);
      return res;
    },
  },
  signOut: async () => {
    const out = await sharedApi.signOut(api);
    writeSessionToken(null);
    return out;
  },
  getSession: async () => handleApiResponse(sharedApi.getSession(api)),
  forgotPassword: async (body: components["schemas"]["AuthForgotPasswordBody"]) =>
    handleApiResponse(sharedApi.forgotPassword(api, body)),
  resetPassword: async (body: components["schemas"]["AuthResetPasswordBody"]) =>
    handleApiResponse(api.POST("/api/auth/reset-password", { body })),
  organization: {
    setActive: async (body: components["schemas"]["SetActiveOrganizationBody"]) =>
      handleApiResponse(api.POST("/api/org/setActiveOrganization", { body })),
  },
};

/**
 * Centralized hook to execute the standard atomic sign-out flow in the web app.
 * Ensures the exact same behavior everywhere (Atomic Clear -> Machine -> UI -> Background Cleanup).
 */
export function useExecuteSignOut() {
  const navigate = useNavigate();
  const { disconnectAndClear, destroyAccountData } = usePowerSyncContext();

  return () => {
    // Capture THIS TAB's active account synchronously (from the URL `/u/<n>`
    // index) before we mutate the machine.
    const signingOut = readActiveAccountSync();

    // Multi-account: figure out synchronously whether ANOTHER account remains
    // after this one signs out. If so, we fall back to it (Gmail style) instead
    // of dumping the user on the login screen — without wiping its data.
    const stateAfterRemove = signingOut
      ? removeAccountReducer(readAccountsStateSync(), signingOut.userId)
      : readAccountsStateSync();
    const fallback = signingOut ? getActiveAccount(stateAfterRemove) : null;
    const fallbackIndex = fallback ? indexOfUserId(stateAfterRemove, fallback.userId) : null;

    // 0. SET INTENT FLAG: Prevent `useSession` from refetching when the cache is cleared.
    setIsSigningOutIntent(true);

    // 1. ATOMIC CLEAR: Wipe storage immediately and synchronously
    // This is the most critical step to prevent the "refresh bug".
    clearSessionStorage();
    writeSessionToken(null);

    // 2. Mark session as logged out in the machine (reactive updates)
    sessionActor.send({ type: "LOGOUT" });

    // 3. Stop any ongoing network requests & wipe all caches
    void queryClient.cancelQueries();
    queryClient.clear();
    queryClient.getMutationCache().clear();

    // 4. Drop the signed-out account from the SHARED registry up front so the
    //    account switcher in THIS tab stops listing it IMMEDIATELY. Doing it here
    //    — before the slow network revoke — plus the same-tab `accounts-changed`
    //    event (dispatched by the store write) fixes the "signed-out account
    //    lingers in the switcher until I click it" bug. Fire-and-forget: the
    //    underlying localStorage write is synchronous.
    if (signingOut?.kind === "real") {
      void accountsStore.remove(signingOut.userId);
    }

    // 5. Navigate this tab: to the remaining account's `/u/<index>`, or to login
    //    if none remains. Indices shift on removal, so we use the recomputed one.
    if (fallback && fallbackIndex !== null) {
      rememberLastActiveIndex(fallbackIndex);
      // Typed param form: a flat `to: "/u/<m>/"` is a no-op when we're already
      // inside the `/u/$u` tree (TanStack resolves it against the current match).
      void navigate({ to: "/u/$u", params: { u: fallbackIndex }, replace: true });
    } else {
      void navigate({ to: "/login", replace: true });
    }

    // 6. Heavy cleanup in background (non-blocking)
    void (async () => {
      try {
        // Revoke server session for the account being signed out.
        await auth.signOut();
        // Wipe the local database of the SIGNED-OUT account ONLY, addressed by
        // its userId. We must NOT use `disconnectAndClear()` here: by now step 5
        // has switched the ACTIVE db to the fallback account (or the guest), so an
        // active-db clear would wipe the WRONG database — that was the reported
        // "signing out of one account erased the guest's data" bug. `signingOut`
        // is captured per-tab BEFORE any mutation, so this is race-free.
        await (signingOut ? destroyAccountData(signingOut.userId) : disconnectAndClear());
        // Re-prime the fallback account so the app lands authenticated as it
        // (its data/token were never touched). Clearing the sign-out intent lets
        // the session query verify it again. Allow the intent reset only once a
        // real fallback exists; otherwise we stay in visitor/login mode.
        if (fallback?.kind === "real") {
          setIsSigningOutIntent(false);
          primeSessionForAccount(fallback);
        }
        console.info("[Auth] Background cleanup completed.");
      } catch (err) {
        console.warn("[Auth] Background cleanup failed (expected if interrupted):", err);
      }
    })();
  };
}
